import React, { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { apiService, Receipt } from '../services/api'
import { money, fmtDateTime, toLocalInput, PayerPill, Spinner } from '../lib/ui'

interface Props {
  receiptId: number | null
  onClose: () => void
}

interface EditState {
  company: string
  total_amount: string
  datetime: string
  payer: 'David' | 'Timna'
  is_shared: boolean
  notes: string
}

const ReceiptDetailOverlay: React.FC<Props> = ({ receiptId, onClose }) => {
  const queryClient = useQueryClient()
  const [shown, setShown] = useState<number | null>(null)
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<EditState | null>(null)
  const [imgUrl, setImgUrl] = useState<string | null>(null)
  const [imgError, setImgError] = useState(false)
  const active = receiptId !== null

  // Keep content mounted briefly so the exit animation can play.
  useEffect(() => {
    if (receiptId !== null) {
      setShown(receiptId)
      setEditing(false)
    } else {
      const t = setTimeout(() => setShown(null), 200)
      return () => clearTimeout(t)
    }
  }, [receiptId])

  const { data: receipt, isLoading } = useQuery({
    queryKey: ['receipt', shown],
    queryFn: () => apiService.getReceipt(shown as number),
    enabled: shown !== null,
  })

  const { data: storesData } = useQuery({
    queryKey: ['stores'],
    queryFn: apiService.getStores,
  })
  const stores = storesData?.stores || []

  // The image endpoint is Basic-auth protected; a plain <img src> request
  // can't carry the Authorization header, so the browser would pop its own
  // login dialog. Fetch the blob via the authenticated client instead and
  // display it as an object URL.
  useEffect(() => {
    setImgUrl(null)
    setImgError(false)
    if (shown === null) return
    let url: string | null = null
    let cancelled = false
    apiService
      .getReceiptImage(shown)
      .then((blob) => {
        if (cancelled) return
        url = URL.createObjectURL(blob)
        setImgUrl(url)
      })
      .catch(() => {
        if (!cancelled) setImgError(true)
      })
    return () => {
      cancelled = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [shown])

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['receipts'] })
    queryClient.invalidateQueries({ queryKey: ['receipt'] })
    queryClient.invalidateQueries({ queryKey: ['monthly-report'] })
    queryClient.invalidateQueries({ queryKey: ['summary'] })
    queryClient.invalidateQueries({ queryKey: ['stores'] })
    queryClient.invalidateQueries({ queryKey: ['available-years'] })
  }

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: EditState }) =>
      apiService.updateReceipt(id, {
        company: data.company,
        total_amount: parseFloat(data.total_amount) || 0,
        datetime: data.datetime,
        payer: data.payer,
        is_shared: data.is_shared,
        notes: data.notes,
      }),
    onSuccess: () => {
      toast.success('Receipt updated')
      invalidate()
      setEditing(false)
    },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Update failed'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteReceipt(id),
    onSuccess: () => {
      toast.success('Receipt deleted')
      invalidate()
      onClose()
    },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Delete failed'),
  })

  const startEdit = (r: Receipt) => {
    setForm({
      company: r.company,
      total_amount: String(r.total_amount),
      datetime: toLocalInput(r.date),
      payer: r.payer,
      is_shared: r.is_shared,
      notes: r.notes || '',
    })
    setEditing(true)
  }

  const onBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    const t = e.target as HTMLElement
    if (t.classList.contains('overlay') || t.classList.contains('panel')) onClose()
  }

  return (
    <div className={`overlay${active ? ' active' : ''}`} onClick={onBackdrop}>
      <div className="panel">
        {shown !== null && (
          <>
            <div className="panel-head">
              <div className="back" onClick={editing ? () => setEditing(false) : onClose}>
                ← {editing ? 'Cancel' : 'Back'}
              </div>
            </div>

            {isLoading || !receipt ? (
              <div className="glass-card"><div className="center-pad"><Spinner /></div></div>
            ) : !editing ? (
              <div className="glass-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div className="muted" style={{ fontSize: 13 }}>Store</div>
                    <div style={{ fontSize: 24, fontWeight: 800 }}>{receipt.company}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div className="muted" style={{ fontSize: 13 }}>Amount</div>
                    <div style={{ fontSize: 24, fontWeight: 800 }}>{money(receipt.total_amount)}</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 18 }}>
                  <div>
                    <div className="tiny muted">Date &amp; time</div>
                    <div style={{ fontWeight: 600, marginTop: 3 }}>{fmtDateTime(receipt.date)}</div>
                  </div>
                  <div>
                    <div className="tiny muted">Paid by</div>
                    <div style={{ marginTop: 5 }}><PayerPill payer={receipt.payer} /></div>
                  </div>
                  <div>
                    <div className="tiny muted">Split type</div>
                    <div style={{ marginTop: 5 }}>
                      <span className={`pill pill-${receipt.is_shared ? 'shared' : 'personal'}`}>
                        {receipt.is_shared ? 'Shared 50/50' : 'Personal — other owes full'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="tiny muted">Receipt #</div>
                    <div style={{ fontWeight: 600, marginTop: 3 }}>#{receipt.id}</div>
                  </div>
                </div>
                <div style={{ marginTop: 16 }}>
                  <div className="tiny muted">Notes</div>
                  <div style={{ marginTop: 4 }}>
                    {receipt.notes ? <em>“{receipt.notes}”</em> : <span className="muted">—</span>}
                  </div>
                </div>
                <div style={{ marginTop: 16 }}>
                  <div className="tiny muted" style={{ marginBottom: 6 }}>Receipt image</div>
                  <div className="imgph">
                    {imgError ? (
                      'No image for this receipt'
                    ) : imgUrl ? (
                      <img src={imgUrl} alt="Receipt" />
                    ) : (
                      <Spinner />
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                  <button className="cta" style={{ flex: 1, justifyContent: 'center' }} onClick={() => startEdit(receipt)}>Edit</button>
                  <button
                    className="cta danger"
                    onClick={() => { if (window.confirm('Delete this receipt?')) deleteMutation.mutate(receipt.id) }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ) : form ? (
              <div className="glass-card">
                <h2 className="page" style={{ fontSize: 20 }}>Edit receipt</h2>
                <div className="field">
                  <label>Store / company</label>
                  <input list="storelist-edit" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
                  <datalist id="storelist-edit">{stores.map((s) => <option key={s} value={s} />)}</datalist>
                </div>
                <div className="field">
                  <label>Amount (€)</label>
                  <input type="number" step="0.01" value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: e.target.value })} />
                </div>
                <div className="field">
                  <label>Date &amp; time</label>
                  <input type="datetime-local" value={form.datetime} onChange={(e) => setForm({ ...form, datetime: e.target.value })} />
                </div>
                <div className="field">
                  <label>Who paid?</label>
                  <div className="radios">
                    <div className={`radio${form.payer === 'David' ? ' on' : ''}`} onClick={() => setForm({ ...form, payer: 'David' })}>D · David</div>
                    <div className={`radio${form.payer === 'Timna' ? ' on' : ''}`} onClick={() => setForm({ ...form, payer: 'Timna' })}>T · Timna</div>
                  </div>
                </div>
                <div className="field">
                  <label>Split type</label>
                  <div className="switch">
                    <div>
                      <div style={{ fontWeight: 600 }}>{form.is_shared ? 'Shared 50/50' : 'Personal'}</div>
                      <div className="tiny muted">{form.is_shared ? 'split evenly' : 'other person owes full amount'}</div>
                    </div>
                    <div className={`toggle${form.is_shared ? ' on' : ''}`} onClick={() => setForm({ ...form, is_shared: !form.is_shared })} />
                  </div>
                </div>
                <div className="field">
                  <label>Notes</label>
                  <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                  <button
                    className="cta"
                    style={{ flex: 1, justifyContent: 'center' }}
                    disabled={updateMutation.isLoading}
                    onClick={() => updateMutation.mutate({ id: receipt!.id, data: form })}
                  >
                    {updateMutation.isLoading ? 'Saving…' : 'Save changes'}
                  </button>
                  <button className="cta ghost" onClick={() => setEditing(false)}>Cancel</button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}

export default ReceiptDetailOverlay
